import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, CheckCircle2, Circle, ExternalLink, GitBranch, Globe, Clock, Sparkles } from "lucide-react";

const statusColor = {
  planning: "text-[#C5C6C7] border-[#C5C6C7]/40",
  active: "text-[#FF3355] border-[#FF3355]",
  paused: "text-[#FFCB6B] border-[#FFCB6B]/60",
  shipped: "text-[#B91C2C] border-[#B91C2C]",
};

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function formatRelative(d) {
  if (!d) return "";
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function ClientProject() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [res, upd] = await Promise.all([
          api.get(`/projects/${id}`),
          api.get(`/projects/${id}/updates`).catch(() => ({ data: [] })),
        ]);
        setData(res.data);
        setUpdates(upd.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load project.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 font-mono text-sm text-[#C5C6C7]">&gt; loading<span className="te-caret">_</span></div>;
  if (err) return <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 font-mono text-sm text-[#FF9D9D]" data-testid="project-error">ERROR · {String(err)}</div>;
  if (!data) return null;

  const { project, milestones, activity } = data;
  const done = milestones.filter((m) => m.completed).length;

  return (
    <div className="bg-[#0B0C10]" data-testid="client-project-page">
      {/* Header */}
      <section className="border-b border-[#1F2833] py-12">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Link to="/portal" className="font-mono text-xs uppercase tracking-[0.22em] text-[#C5C6C7] hover:text-[#FF3355] inline-flex items-center gap-2" data-testid="back-to-portal">
            <ArrowLeft size={14} /> back to portal
          </Link>
          <div className="mt-6 flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className={`inline-flex font-mono text-[10px] uppercase tracking-[0.22em] border px-2 py-1 mb-4 ${statusColor[project.status]}`} data-testid="project-status">
                {project.status}
              </div>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter">{project.name}</h1>
              {project.description && (
                <p className="mt-5 text-[#C5C6C7] max-w-3xl leading-relaxed">{project.description}</p>
              )}
            </div>
          </div>

          {/* Preview links */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#1F2833] border border-[#1F2833]">
            <PreviewCard label="staging" url={project.preview_staging_url} icon={Globe} />
            <PreviewCard label="production" url={project.preview_production_url} icon={Globe} />
            <PreviewCard label="repository" url={project.repository_url} icon={GitBranch} />
          </div>

          {/* Progress */}
          <div className="mt-10">
            <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7] mb-2">
              <span>sprint velocity</span>
              <span className="text-[#FF3355]" data-testid="project-progress">{project.sprint_progress}%</span>
            </div>
            <div className="h-2 bg-[#1F2833] relative overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#B91C2C] to-[#FF3355]" style={{ width: `${project.sprint_progress}%` }} />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Milestones */}
          <div className="lg:col-span-7" data-testid="milestones-panel">            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C]">// MILESTONES</div>
                <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-tight mt-2">
                  {done}<span className="text-[#C5C6C7]">/{milestones.length}</span> complete
                </h2>
              </div>
            </div>
            {milestones.length === 0 ? (
              <div className="font-mono text-sm text-[#C5C6C7] border border-[#1F2833] p-6">&gt; no milestones yet</div>
            ) : (
              <ul className="border border-[#1F2833] divide-y divide-[#1F2833]">
                {milestones.map((m) => (
                  <li key={m.id} className="px-5 py-4 flex items-start gap-4" data-testid={`milestone-${m.id}`}>
                    {m.completed ? (
                      <CheckCircle2 size={20} className="text-[#FF3355] shrink-0 mt-0.5" />
                    ) : (
                      <Circle size={20} className="text-[#C5C6C7]/40 shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <div className={`font-display text-base font-semibold ${m.completed ? "line-through text-[#C5C6C7]" : "text-[#F5F5F7]"}`}>
                        {m.title}
                      </div>
                      {m.description && <div className="mt-1 text-sm text-[#C5C6C7]">{m.description}</div>}
                    </div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#C5C6C7]/70 flex items-center gap-1.5 shrink-0">
                      <Clock size={12} />
                      {formatDate(m.due_date)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Activity */}
          <div className="lg:col-span-5" data-testid="activity-panel">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6">// ACTIVITY.LOG</div>
            {activity.length === 0 ? (
              <div className="font-mono text-sm text-[#C5C6C7] border border-[#1F2833] p-6">&gt; no activity yet</div>
            ) : (
              <ol className="border-l-2 border-[#1F2833] space-y-6 pl-6">
                {activity.map((a) => (
                  <li key={a.id} className="relative" data-testid={`activity-${a.id}`}>
                    <span className="absolute -left-[33px] top-1.5 h-3 w-3 bg-[#FF3355] border-2 border-[#0B0C10]" />
                    <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#B91C2C]">
                      {a.author} · {formatRelative(a.created_at)}
                    </div>
                    <div className="mt-1 text-sm text-[#F5F5F7]">{a.message}</div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </section>

      {/* AI-generated progress updates */}
      <section className="py-12 lg:py-16 border-t border-[#1F2833]" data-testid="updates-panel">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-8">
            <Sparkles size={16} className="text-[#FF3355]" />
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C]">// PROGRESS.UPDATES · {updates.length}</div>
          </div>
          {updates.length === 0 ? (
            <div className="font-mono text-sm text-[#C5C6C7] border border-[#1F2833] p-6">&gt; no updates published yet — check back soon</div>
          ) : (
            <div className="space-y-6">
              {updates.map((u) => (
                <article key={u.id} className="border border-[#1F2833] bg-[#0d1117] p-6 lg:p-8" data-testid={`update-${u.id}`}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B91C2C]">
                    {new Date(u.created_at).toLocaleString()} · progress update
                  </div>
                  <h3 className="mt-3 font-display text-2xl font-bold tracking-tight">{u.title}</h3>
                  {u.summary && <p className="mt-2 text-[#FF3355] font-display text-lg">{u.summary}</p>}
                  <div className="mt-5 prose prose-invert max-w-none text-[#C5C6C7] leading-relaxed" dangerouslySetInnerHTML={{ __html: u.body_html }} />
                  {u.screenshots?.length > 0 && (
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {u.screenshots.map((src, i) => (
                        <a key={i} href={src} target="_blank" rel="noreferrer" className="block border border-[#1F2833] overflow-hidden hover:border-[#FF3355]">
                          <img src={src} alt={`screenshot ${i + 1}`} className="w-full h-auto" />
                        </a>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function PreviewCard({ label, url, icon: Icon }) {
  const isLive = !!url;
  const content = (
    <div className={`bg-[#0B0C10] p-5 ${isLive ? "te-glow-hover hover:border-[#B91C2C] border border-transparent" : "opacity-60"}`}>
      <div className="flex items-center justify-between">
        <Icon size={18} className={isLive ? "text-[#FF3355]" : "text-[#C5C6C7]/40"} />
        {isLive && <ExternalLink size={14} className="text-[#FF3355]" />}
      </div>
      <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]">{label}</div>
      <div className={`mt-1 font-mono text-sm truncate ${isLive ? "text-[#F5F5F7]" : "text-[#C5C6C7]/50"}`}>
        {url || "— not yet deployed —"}
      </div>
    </div>
  );
  return isLive ? (
    <a href={url} target="_blank" rel="noreferrer" data-testid={`preview-${label}`}>{content}</a>
  ) : (
    <div data-testid={`preview-${label}`}>{content}</div>
  );
}
